import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import { patientEvolutionsService, PatientEvolution } from '../services/patientEvolutionsService';
import { patientsService, PatientDetail } from '../services/patientsService';
import { printTemplateService, renderTemplateHtml, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { titleCaseName } from '../utils/nameCase';
import { colors } from '../theme/designSystem';

/**
 * Impressão das evoluções selecionadas (?page=evolutions-print&patientId=&ids=): identificação
 * do paciente no corpo e cabeçalho/rodapé vindos do TEMPLATE PADRÃO da entidade, repetidos em
 * toda página por thead/tfoot (layout da impressão de avaliação). As observações de uso
 * interno NÃO entram no papel.
 */
const PRINT_PAGE_SIZE = 100;

const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const timeOf = (iso: string): string => iso.slice(11, 16);

/** Percorre as páginas da lista até cobrir o total, ficando só com os ids pedidos. */
const loadSelectedEvolutions = async (patientId: string, wanted: Set<string>): Promise<PatientEvolution[]> => {
  const found: PatientEvolution[] = [];
  let pageNumber = 1;
  let totalCount = Infinity;
  while (found.length < wanted.size && (pageNumber - 1) * PRINT_PAGE_SIZE < totalCount) {
    const result = await patientEvolutionsService.getByPatient(patientId, { pageNumber, pageSize: PRINT_PAGE_SIZE });
    totalCount = result.totalCount;
    found.push(...result.items.filter(e => wanted.has(e.id)));
    if (result.items.length === 0) break;
    pageNumber += 1;
  }
  return found;
};

const Field: React.FC<{ label: string; value: string | null | undefined; wide?: boolean }> = ({ label, value, wide }) => {
  if (!value) return null;
  return (
    <Typography sx={{ fontSize: '10.5px', color: '#212529', lineHeight: 1.5, gridColumn: wide ? '1 / -1' : undefined, overflowWrap: 'anywhere' }}>
      <strong>{label}:</strong> {value}
    </Typography>
  );
};

const EvolutionsPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();
  const patientId = getParam('patientId') || '';
  const idsParam = getParam('ids') || '';

  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [evolutions, setEvolutions] = useState<PatientEvolution[]>([]);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'evolutions');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Clinic4Us - Impressão de Evoluções';
    const load = async () => {
      try {
        const wanted = new Set(idsParam.split(',').filter(Boolean));
        const [patientData, selected, templates] = await Promise.all([
          patientsService.getById(patientId),
          loadSelectedEvolutions(patientId, wanted),
          // Sem template (ou sem permissão de lê-lo), a impressão sai sem cabeçalho/rodapé — não falha
          user?.entityId ? printTemplateService.getByEntity(user.entityId).catch(() => []) : Promise.resolve([]),
        ]);
        setPatient(patientData);
        // Impresso em ordem cronológica (a lista da tela é decrescente; registro se lê do começo)
        setEvolutions(selected
          .filter(e => e.canRead)
          .sort((a, b) => a.sessionDate.localeCompare(b.sessionDate)));
        setTemplates(templates);
      } catch {
        setError('Não foi possível carregar os dados para impressão.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, idsParam]);

  const birthDate = patient?.patientBirthDate
    ? formatIsoDate(patient.patientBirthDate.slice(0, 10))
    : '—';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (error || !patient) {
    return (
      <Typography sx={{ color: colors.error, textAlign: 'center', py: 8 }}>
        {error || 'Paciente não encontrado.'}
      </Typography>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '2rem', '@media print': { padding: 0, backgroundColor: '#fff' } }}>
      {/* Cabeçalho em todas as páginas pelo thead; rodapé fixo no pé de cada folha (position fixed
          no papel), com a altura reservada pelo tfoot. */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          .evolutions-print-page {
            padding: 0 !important;
            width: auto !important;
            min-height: auto !important;
            box-shadow: none !important;
          }
          .print-frame { width: 100%; border-collapse: collapse; }
          .print-frame > thead { display: table-header-group; }
          .print-frame > tfoot { display: table-footer-group; }
          .print-frame > thead > tr > td,
          .print-frame > tbody > tr > td,
          .print-frame > tfoot > tr > td { padding: 0; }
          .print-frame-header img { max-height: 18mm !important; }
          tr { break-inside: avoid; }
          /* Rodapé no pé de TODA folha, inclusive a última: no tfoot ele ficava colado ao fim do
             conteúdo. O tfoot passa a só reservar a altura, para o texto não correr por baixo. */
          .print-frame-footer { position: fixed !important; left: 0; right: 0; bottom: 0; }
          .print-frame-footer-space { display: block !important; height: 24mm; }
        }
      `}</style>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, maxWidth: '210mm', mx: 'auto', '@media print': { display: 'none' } }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={() => window.print()}
          sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}
        >
          Imprimir
        </Button>
      </Box>

      <Box
        className="evolutions-print-page"
        sx={{
          width: '210mm',
          maxWidth: '100%',
          minHeight: '297mm',
          padding: '15mm',
          paddingBottom: '30mm',
          boxSizing: 'border-box',
          background: 'white',
          margin: '0 auto',
          position: 'relative',
          boxShadow: '0 0 8px rgba(0,0,0,0.15)',
          fontSize: '12pt',
          color: '#000',
          '@media print': { boxShadow: 'none', width: 'auto', minHeight: 'auto', margin: 0 },
        }}
      >
        {/* Largura no elemento: a regra de @media print não vale na pré-visualização em tela */}
        <table className="print-frame" style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <thead><tr><td>
            {template && (
<div className="print-frame-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10mm' }}>
                <div style={{ flexShrink: 0 }}>
                  {template?.file ? (
                    <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} />
                  ) : null}
                </div>
                <div
                  style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }}
                  dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.header) }}
                />
              </div>
              <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
            </div>
)}
          </td></tr></thead>

          <tfoot><tr><td>
            {template && <div className="print-frame-footer-space" style={{ display: 'none' }} />}
          </td></tr></tfoot>

          <tbody><tr><td>
            <Box sx={{ textAlign: 'center', mb: '6mm' }}>
              <Typography sx={{ fontSize: '13pt', fontWeight: 700, color: '#212529' }}>
                Evoluções
              </Typography>
            </Box>

            <Box sx={{ mb: '6mm' }}>
              <Box sx={{ display: 'flex', gap: '3rem', mb: '4px', flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '11px', color: '#212529' }}>
                  Paciente: {patient.patientName}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: '#212529' }}>
                  Data Nasc.: {birthDate}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '11px', color: '#212529', mb: '4px' }}>
                Responsável: {patient.responsibleName1 || '—'}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#212529' }}>
                Data de emissão: {new Date().toLocaleDateString('pt-BR')}
              </Typography>
            </Box>

            {evolutions.length === 0 ? (
              <Typography sx={{ fontSize: '11px', color: '#6c757d' }}>
                Nenhuma evolução selecionada para impressão.
              </Typography>
            ) : (
              evolutions.map(evolution => (
                <Box
                  key={evolution.id}
                  sx={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden', mb: '4mm', breakInside: 'avoid' }}
                >
                  <Box sx={{ backgroundColor: '#e9ecef', borderBottom: '1px solid #dee2e6', p: '6px 8px', display: 'flex', gap: '1rem', flexWrap: 'wrap', breakAfter: 'avoid' }}>
                    <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: '#212529' }}>
                      {formatIsoDate(evolution.sessionDate)}
                      {evolution.kind === 'standalone' ? ' · Evolução avulsa' : ` ${timeOf(evolution.sessionDate)}`}
                    </Typography>
                    {/* Avulsa não tem profissional do atendimento: identifica quem registrou */}
                    {evolution.kind === 'standalone' && (
                      <Typography sx={{ fontSize: '10.5px', color: '#212529' }}>
                        {titleCaseName(evolution.createdByName)}
                        {evolution.createdByDocument && ` · Registro ${evolution.createdByDocument}`}
                      </Typography>
                    )}
                    {evolution.professionalName && (
                      <Typography sx={{ fontSize: '10.5px', color: '#212529' }}>
                        {titleCaseName(evolution.professionalName)}
                        {evolution.professionalDocument && ` · Registro ${evolution.professionalDocument}`}
                      </Typography>
                    )}
                    {evolution.serviceTypeName && (
                      <Typography sx={{ fontSize: '10.5px', color: '#495057' }}>{evolution.serviceTypeName}</Typography>
                    )}
                  </Box>

                  <Box sx={{ p: '8px 10px' }}>
                    {/* Duas colunas para economizar papel; objetivo e justificativa, que são longos, ocupam a linha */}
                    <Box sx={{ mb: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '12px' }}>
                      {/* Sem plano avaliado, a linha do objetivo não sai (mesma regra do detalhe da aba) */}
                      {!evolution.disregardPlan && (
                        <Typography sx={{ gridColumn: '1 / -1', fontSize: '10.5px', color: '#212529', lineHeight: 1.5 }}>
                          <strong>Objetivo:</strong> {evolution.planPeriodTitle ? `${evolution.planPeriodTitle} · ` : ''}{evolution.planTitle || '—'}
                          {evolution.planEvolutionScale !== null && ` · nota ${evolution.planEvolutionScale}/10`}
                          {evolution.planCurrentScale !== null && evolution.planTargetScale !== null
                            && ` (estado atual ${evolution.planCurrentScale} → meta ${evolution.planTargetScale})`}
                        </Typography>
                      )}
                      <Field label="Situação" value={evolution.patientSituationName} />
                      <Field label="Condição" value={evolution.patientConditionName} />
                      <Field label="Justificativa da condição" value={evolution.patientConditionJustification} wide />
                      <Field label="Abordagem" value={evolution.aproachName} />
                      <Field label="Recurso" value={evolution.therapeuticResourceName} />
                      <Field label="Escala GAS" value={evolution.gasScale} wide />
                    </Box>

                    {/* Mesmo formato "Rótulo: valor" dos campos acima; o texto longo quebra linha como foi escrito */}
                    <Typography sx={{ fontSize: '10.5px', color: '#212529', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>
                      <strong>Evolução terapêutica:</strong> {evolution.therapeuticEvolution || '—'}
                    </Typography>
                    {evolution.guidance && (
                      <Typography sx={{ fontSize: '10.5px', color: '#212529', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>
                        <strong>Orientação de conduta:</strong> {evolution.guidance}
                      </Typography>
                    )}

                    <Typography sx={{ fontSize: '9.5px', color: '#6c757d', mt: '6px' }}>
                      {/* Só quem registrou; data e hora da inclusão ficam no detalhe da tela (Hilton, 28/09/2026) */}
                      Registrado por {titleCaseName(evolution.createdByName)}
                      {evolution.createdByDocument && ` · Registro ${evolution.createdByDocument}`}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
          </td></tr></tbody>
        </table>
        {template && (
          <div
            className="print-frame-footer"
            style={{ position: 'absolute', left: '15mm', right: '15mm', bottom: '10mm', textAlign: 'center', fontSize: '9pt' }}
            dangerouslySetInnerHTML={{ __html: renderTemplateHtml(template?.footer) }}
          />
        )}
      </Box>
    </Box>
  );
};

export default EvolutionsPrint;
